import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Trophy, 
  XCircle, 
  CheckCircle,
  Home,
  Download,
  Share2,
  Award
} from 'lucide-react';
import confetti from 'canvas-confetti';

const ResultsPage = () => {
  const { attemptId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [result, setResult] = useState(location.state?.result || null);

  useEffect(() => {
    if (!result) {
      // Fetch result from API if not in state
      navigate('/assessment');
      return;
    }

    // Trigger confetti for pass
    if (result.result === 'pass') {
      setTimeout(() => {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
      }, 500);
    }
  }, [result, navigate]);

  if (!result) {
    return null;
  }

  const isPassed = result.result === 'pass';

  return (
    <div className={`min-h-screen ${isPassed ? 'bg-gradient-to-br from-emerald-50 to-blue-50' : 'bg-gradient-to-br from-slate-50 to-red-50'} flex items-center justify-center p-4`}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className={`p-8 text-center ${isPassed ? 'bg-gradient-to-r from-emerald-500 to-blue-500' : 'bg-gradient-to-r from-slate-500 to-red-500'}`}>
          <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-4 ${isPassed ? 'bg-white/20' : 'bg-white/20'}`}>
            {isPassed ? (
              <Trophy className="h-12 w-12 text-white" />
            ) : (
              <XCircle className="h-12 w-12 text-white" />
            )}
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {isPassed ? 'Congratulations!' : 'Better Luck Next Time'}
          </h1>
          <p className="text-white/90">
            {isPassed ? 'You have passed the assessment!' : 'You did not pass this time.'}
          </p>
        </div>

        {/* Score Display */}
        <div className="p-8">
          <div className="text-center mb-6">
            <div className={`inline-flex items-center justify-center w-32 h-32 rounded-full border-8 ${
              isPassed ? 'border-emerald-500' : 'border-red-500'
            }`}>
              <div>
                <p className={`text-4xl font-bold ${isPassed ? 'text-emerald-600' : 'text-red-600'}`}>
                  {result.percentage.toFixed(0)}%
                </p>
                <p className="text-sm text-slate-500">Score</p>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-slate-900">{result.marks_obtained}</p>
              <p className="text-xs text-slate-500">Marks Obtained</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-slate-900">{result.total_marks}</p>
              <p className="text-xs text-slate-500">Total Marks</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-emerald-600">{result.correct_answers}</p>
              <p className="text-xs text-slate-500">Correct</p>
            </div>
          </div>

          {/* Detailed Stats */}
          <div className="space-y-3 mb-6">
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
                <span className="text-slate-700">Correct Answers</span>
              </div>
              <span className="font-bold text-emerald-600">{result.correct_answers}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-600" />
                <span className="text-slate-700">Wrong Answers</span>
              </div>
              <span className="font-bold text-red-600">{result.wrong_answers}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            {isPassed && result.certificate_id && (
              <Button 
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                onClick={() => navigate(`/assessment/certificate/${result.certificate_id}`)}
                data-testid="view-certificate-btn"
              >
                <Award className="h-4 w-4 mr-2" />
                View & Download Certificate
              </Button>
            )}
            
            <Button 
              variant="outline"
              className="w-full"
              onClick={() => navigate('/assessment')}
              data-testid="back-to-portal-btn"
            >
              <Home className="h-4 w-4 mr-2" />
              Back to Assessment Portal
            </Button>
          </div>

          {!isPassed && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Don't give up!</strong> Review your course material and try again. 
                You need 50% to pass. Contact support if you need help.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;
